import { useQueryClient } from '@tanstack/react-query';
import { Timeline, toastNotifications } from '@uag/react-core';
import {
    GetContractSigningLinkResponse,
    GetStepsResponse,
    StepActionModel,
    StepActionType,
    StepProgress,
    StepType,
} from '@uag/welcome-shared/src/api/models';
import {
    useDownloadContractFilesZipContentHook,
    useGetContractSigningLinkHook,
} from '@uag/welcome-shared/src/api/queries/contracts/contracts';
import { getGetRequestByIdQueryKey } from '@uag/welcome-shared/src/api/queries/requests/requests';
import { getGetStepsQueryKey, useGetSteps, useGetStepsHook } from '@uag/welcome-shared/src/api/queries/steps/steps';
import { downloadFileWithHook } from '@uag/welcome-shared/src/utils';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';

import { withRetry } from 'shared/utils';
import { openChooseSignerModal } from '../modals/ChooseSignerModal';
import { SignerType } from '../modals/components/SelectSigner';
import { openEditCompanyModal } from '../modals/EditCompanyModal';
import { openViewCompanyModal } from '../modals/ViewCompanyModal';
import { getActionIcon } from './utils';

const POLLING_INTERVAL = 5000;

type Props = {
    stepAction: StepActionModel;
    stepProgress: StepProgress;
};

async function waitForStepProgress(
    requestId: string,
    getSteps: (id: string) => Promise<GetStepsResponse>,
    stepType: StepType,
    stepProgress: StepProgress,
) {
    const maxAttempts = 5;
    let delay = 500;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        const response = await getSteps(requestId);
        const currentStep = response.data.find((s) => s.type === stepType);

        if (currentStep?.progress === stepProgress) {
            return;
        }

        if (attempt < maxAttempts) {
            await new Promise((res) => setTimeout(res, delay));
            delay *= 2;
        }
    }
    throw new Error('Step did not reach the expected progress in time');
}

export const StepAction = ({ stepAction, stepProgress }: Props) => {
    const { t } = useTranslation('translations', { keyPrefix: 'steps.actions' });
    const { t: rootT } = useTranslation('translations');
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const { requestId = '' } = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const getSteps = useGetStepsHook();
    const getContractSigningLink: (id: string) => Promise<GetContractSigningLinkResponse> =
        useGetContractSigningLinkHook();

    const downloadContractFiles = useDownloadContractFilesZipContentHook();

    const event = searchParams.get('event');

    const inContractLinkPolling =
        event === 'signing_complete'
        && stepProgress === StepProgress.InProgress
        && stepAction.actionType === StepActionType.SignContractLink;

    // Poll steps while returning from signing; hook result is intentionally unused.
    useGetSteps(requestId, { query: { refetchInterval: inContractLinkPolling ? POLLING_INTERVAL : false } });

    const handleDownloadContract = () => {
        setIsLoading(true);
        downloadFileWithHook(downloadContractFiles(requestId), 'contract.zip')
            .catch((err) => {
                toastNotifications.errorDetail(
                    {
                        title: rootT('errorDownloadingContract'),
                    },
                    err,
                );
            })
            .finally(() => setIsLoading(false));
    };

    const openLink = () => {
        if (stepAction.data?.link) {
            window.open(stepAction.data?.link, '_blank');
        }
    };

    const openContractSigningLink = async () => {
        try {
            setIsLoading(true);

            const getContractSigningLinkWithRetry = withRetry(async (id: string) => {
                const response = await getContractSigningLink(id);
                const contractLink = response?.data?.link;

                // Treat an empty link as a transient failure and retry.
                if (!contractLink?.trim()) {
                    throw new Error('Contract signing link is not ready yet');
                }

                return contractLink;
            });

            const contractLink = await getContractSigningLinkWithRetry(requestId);

            window.open(contractLink, '_self');
        } catch {
            toastNotifications.error({
                title: rootT('failed'),
                message: rootT('dataNotSaved', { data: rootT('signing') }),
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleActionClick = async ({ actionType }: StepActionModel) => {
        switch (actionType) {
            case StepActionType.FileUpload:
                // TODO implement behaviour
                break;
            case StepActionType.FileDownload:
                // TODO implement behaviour
                break;
            case StepActionType.InsertCompanyData:
            case StepActionType.EditCompanyData:
                openEditCompanyModal();
                break;
            case StepActionType.ViewCompanyData:
                openViewCompanyModal();
                break;
            case StepActionType.ActivateMyDoka: {
                setIsLoading(true);
                try {
                    const signerType = await openChooseSignerModal();

                    if (signerType) {
                        await waitForStepProgress(requestId, getSteps, StepType.SignContract, StepProgress.InProgress);
                    }

                    await queryClient.invalidateQueries({ queryKey: getGetStepsQueryKey(requestId) });
                    await queryClient.invalidateQueries({ queryKey: getGetRequestByIdQueryKey(requestId) });

                    if (signerType === SignerType.Self) {
                        await openContractSigningLink();
                    }

                    break;
                } catch {
                    toastNotifications.error({
                        title: rootT('failed'),
                        message: rootT('dataNotSaved', { data: rootT('signing') }),
                    });
                } finally {
                    setIsLoading(false);
                }

                break;
            }
            case StepActionType.SignContractLink:
                await openContractSigningLink();
                break;
            case StepActionType.DownloadContract:
                handleDownloadContract();
                break;
            case StepActionType.MyDokaLink:
                openLink();
                break;
            case StepActionType.OnlineShopLink:
                openLink();
                break;
            default: {
                // This will cause a TypeScript error if a new StepActionType is added and not handled above
                const _exhaustiveCheck: never = actionType;
                return _exhaustiveCheck;
            }
        }
    };

    return (
        <Timeline.Action
            icon={getActionIcon(stepAction.actionType)}
            loading={inContractLinkPolling || isLoading}
            title={t(stepAction.actionType)}
            onClick={() => handleActionClick(stepAction)}
        />
    );
};
